import { tokens } from "../../../../theme";
import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import Select from "../../../../components/SearchableSelect";
import {
  Close,
  ExpandLess,
  ExpandMore,
  PlaylistAddRounded,
} from "@mui/icons-material";
import { refreshDrawerStyles } from "./styles";
import {
  extraKeyError,
  groupNoun,
  isCompleteExtra,
  isOverridden,
  matchesFilter,
  registryOptions,
} from "./helpers";

const ManifestGroup = ({
  group,
  label,
  rows,
  edits,
  extras,
  filter,
  expanded,
  onToggle,
  onEdit,
  onAddExtra,
  onEditExtra,
  onRemoveExtra,
}) => {
  const theme = useTheme();
  const { refreshDrawer, ROW_HEIGHT, MAX_VISIBLE_ROWS } = refreshDrawerStyles(
    tokens(theme.paletteId)
  );

  const noun = groupNoun(group);
  const columns = ["Entry", "Registry", noun.nameColumn, "Tag", ""];
  const visibleRows = rows.filter((row) => matchesFilter(row, filter));
  const overrides = rows.filter((row) =>
    isOverridden(row, edits?.[row.key])
  ).length;
  const added = (extras ?? []).filter(isCompleteExtra).length;

  return (
    <Box sx={refreshDrawer.card}>
      <Box sx={refreshDrawer.cardHeadRow}>
        <Box
          component="button"
          type="button"
          sx={refreshDrawer.cardHeadClickable}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <Typography sx={refreshDrawer.cardTitle}>{label}</Typography>
          <Box sx={refreshDrawer.headBadges}>
            <Box
              component="span"
              sx={refreshDrawer.countBadge}
            >{`${visibleRows.length} of ${rows.length}`}</Box>
            {overrides ? (
              <Box
                component="span"
                sx={refreshDrawer.countBadge}
              >{`${overrides} changed`}</Box>
            ) : null}
            {added ? (
              <Box
                component="span"
                sx={refreshDrawer.countBadge}
              >{`${added} added`}</Box>
            ) : null}
          </Box>
          <Box sx={{ ...refreshDrawer.chevron, ml: "auto" }}>
            {expanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </Box>
        </Box>
        <Tooltip
          title={`Add ${noun.singular === "image" ? "an" : "a"} ${
            noun.singular
          } that is not in the manifest`}
        >
          <IconButton
            sx={refreshDrawer.addRowButton}
            onClick={onAddExtra}
            aria-label={`Add ${noun.singular}`}
          >
            <PlaylistAddRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={refreshDrawer.rowHeader}>
          {columns.map((column, idx) => (
            <Typography key={column || idx} sx={refreshDrawer.rowHeaderCell}>
              {column}
            </Typography>
          ))}
        </Box>

        {(extras ?? []).map((extra, idx) => {
          const keyErr = extraKeyError(extra, rows);
          return (
            <Box key={`extra-${idx}`} sx={refreshDrawer.row(true)}>
              <Box sx={refreshDrawer.rowKeyCell}>
                <Box sx={refreshDrawer.overrideDot} />
                <TextField
                  size="small"
                  placeholder="Entry key"
                  value={extra.key}
                  onChange={(e) =>
                    onEditExtra(group, idx, "key", e.target.value)
                  }
                  sx={refreshDrawer.rowInput}
                  error={Boolean(keyErr)}
                  helperText={keyErr || undefined}
                />
              </Box>
              <Select
                size="small"
                value={extra.registry}
                onChange={(e) =>
                  onEditExtra(group, idx, "registry", e.target.value)
                }
                sx={refreshDrawer.rowSelect}
              >
                {registryOptions(group).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                placeholder={noun.nameColumn}
                value={extra.name}
                onChange={(e) =>
                  onEditExtra(group, idx, "name", e.target.value)
                }
                sx={refreshDrawer.rowInput}
              />
              <TextField
                size="small"
                placeholder="Tag"
                value={extra.tag}
                onChange={(e) => onEditExtra(group, idx, "tag", e.target.value)}
                sx={refreshDrawer.rowInput}
              />
              <IconButton
                sx={refreshDrawer.removeRowButton}
                onClick={() => onRemoveExtra(group, idx)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          );
        })}

        {visibleRows.length ? (
          <Virtuoso
            // A group can hold hundreds of rows; only those near the scroll
            // position are ever mounted. Height only caps the scroll window
            // (every row is still reachable by scrolling within it) — it
            // never truncates `visibleRows` itself, unlike the similar
            // Releases/ReleaseView/PatchBlock.jsx which slices its data and
            // shows a "N more" line instead.
            style={{
              height:
                Math.min(visibleRows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT,
            }}
            data={visibleRows}
            // Rows carry live per-row text edits, and the filter can reorder
            // `visibleRows` while the user is typing — an index-based key
            // would let React reuse an input across two different rows and
            // attach a typed value to the wrong entry.
            computeItemKey={(index) => visibleRows[index]?.key}
            itemContent={(_index, row) => {
              const edited = edits?.[row.key] ?? row;
              const changed = isOverridden(row, edited);
              return (
                <Box sx={refreshDrawer.row(changed)}>
                  <Box sx={refreshDrawer.rowKeyCell}>
                    {changed ? <Box sx={refreshDrawer.overrideDot} /> : null}
                    <Box sx={refreshDrawer.rowKeyText}>
                      <Typography
                        sx={refreshDrawer.rowKey}
                        title={row.repoName || row.key}
                      >
                        {row.key}
                      </Typography>
                      {row.commit ? (
                        <Typography
                          sx={refreshDrawer.rowCommit}
                          title={row.commit}
                        >
                          {row.commit.slice(0, 7)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                  <Select
                    size="small"
                    value={edited.registry}
                    onChange={(e) =>
                      onEdit(group, row.key, "registry", e.target.value)
                    }
                    sx={refreshDrawer.rowSelect}
                  >
                    {registryOptions(group).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    size="small"
                    value={edited.name}
                    onChange={(e) =>
                      onEdit(group, row.key, "name", e.target.value)
                    }
                    sx={refreshDrawer.rowInput}
                  />
                  <TextField
                    size="small"
                    value={edited.tag}
                    onChange={(e) =>
                      onEdit(group, row.key, "tag", e.target.value)
                    }
                    sx={refreshDrawer.rowInput}
                  />
                  <Box sx={refreshDrawer.rowActionSpacer} />
                </Box>
              );
            }}
          />
        ) : extras?.length ? null : (
          <Typography sx={refreshDrawer.emptyRow}>
            No entries match the filter.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

export default ManifestGroup;
